/**
 * NoteMappingPanel - Configure isomorphic note mapping
 * Spec §5.3: Note mapping configuration (origin, base_note, intervals)
 * Spec §5.5: Per-layer note mapping (optional, falls back to global)
 * Spec §5.6: Per-zone note mapping (optional, falls back to global)
 *
 * Scope selector: Global (with per-layer support) or per-zone mapping
 */

import { useState, useEffect } from 'react';
import { useConfigStore, selectMappingMode } from '@/store/configStore';
import { getNoteName, presetMappings } from '@/lib/noteMapping';
import type { NoteMapping } from '@/types/config';

/**
 * Preset layout explanations
 * Each preset has a short description and details about musical intervals
 */
const presetExplanations: Record<keyof typeof presetMappings, {
  name: string;
  description: string;
  intervals: string;
  bestFor: string;
}> = {
  wickiHayden: {
    name: 'Wicki-Hayden',
    description: 'Isomorphic layout designed for diatonic music. Moving right plays whole tones (2 semitones), moving diagonally up-right plays perfect fifths (7 semitones). This creates a pattern where scales flow naturally along rows.',
    intervals: 'Right: +2 (whole tone) | Up-right: +7 (P5) | Up-left: +5 (P4)',
    bestFor: 'Melodic playing, learning scales, diatonic music',
  },
  harmonicTable: {
    name: 'Harmonic Table',
    description: 'Optimized for chord-based playing. Major and minor triads form compact triangular shapes. Moving right plays major thirds (4 semitones), moving diagonally plays perfect fifths (7 semitones).',
    intervals: 'Right: +4 (M3) | Up-right: +7 (P5) | Up-left: +3 (m3)',
    bestFor: 'Chord progressions, harmony exploration, jazz voicings',
  },
  gerhard: {
    name: 'Gerhard',
    description: 'Alternative harmonic layout with fifths along rows. Moving right plays perfect fifths (7 semitones), moving diagonally up-right plays major thirds (4 semitones). Good for exploring harmonic relationships.',
    intervals: 'Right: +7 (P5) | Up-right: +4 (M3) | Up-left: -3 (m3 down)',
    bestFor: 'Harmonic exploration, circle of fifths navigation',
  },
  chromatic: {
    name: 'Chromatic',
    description: 'Compact chromatic layout where all 12 notes appear within a small area. Moving right plays major thirds (4 semitones), moving diagonally up-right plays minor seconds (1 semitone).',
    intervals: 'Right: +4 (M3) | Up-right: +1 (m2) | Up-left: -3 (m3 down)',
    bestFor: 'Chromatic passages, atonal music, 12-tone technique',
  },
  wholeTone: {
    name: 'Whole Tone',
    description: 'Layout based on the whole tone scale. Both horizontal and diagonal movements play whole tones (2 semitones). Creates a symmetrical pattern with only 6 unique pitch classes.',
    intervals: 'Right: +2 (M2) | Up-right: +2 (M2) | Up-left: 0 (unison)',
    bestFor: 'Impressionistic music, Debussy-style passages, ambient textures',
  },
  fourths: {
    name: 'Fourths (Guitar-like)',
    description: 'Tuned in perfect fourths like a bass guitar or the lower strings of a standard guitar. Both horizontal and diagonal movements play perfect fourths (5 semitones).',
    intervals: 'Right: +5 (P4) | Up-right: +5 (P4) | Up-left: 0 (unison)',
    bestFor: 'Guitar players transitioning to hex keyboard, bass lines',
  },
};

/**
 * Detect which preset matches the current mapping (if any)
 */
function detectPreset(mapping: NoteMapping): keyof typeof presetMappings | null {
  for (const [key, preset] of Object.entries(presetMappings)) {
    if (
      mapping.intervals.q === preset.intervals.q &&
      mapping.intervals.r === preset.intervals.r
    ) {
      return key as keyof typeof presetMappings;
    }
  }
  return null;
}

export function NoteMappingPanel() {
  // Global note mapping (used as default/fallback)
  const globalMapping = useConfigStore((state) => state.noteMapping);
  const setNoteMapping = useConfigStore((state) => state.setNoteMapping);
  const updateOrigin = useConfigStore((state) => state.updateNoteMappingOrigin);
  const updateBase = useConfigStore((state) => state.updateNoteMappingBase);
  const updateIntervals = useConfigStore(
    (state) => state.updateNoteMappingIntervals
  );

  // Microtonal tuning state (v2.0)
  const mappingMode = useConfigStore(selectMappingMode);
  const isMicrotonal = mappingMode === 'microtonal';

  // Layers
  const layers = useConfigStore((state) => state.layers);
  const activeLayer = useConfigStore((state) => state.activeLayer);
  const setLayerNoteMapping = useConfigStore((state) => state.setLayerNoteMapping);

  // Zones
  const zones = useConfigStore((state) => state.zones);
  const updateZone = useConfigStore((state) => state.updateZone);
  const setZonePreview = useConfigStore((state) => state.setZonePreview);

  // Scope: 'global' or zone index
  const [scope, setScope] = useState<'global' | number>('global');

  // Reset scope if selected zone was deleted
  useEffect(() => {
    if (typeof scope === 'number' && scope >= zones.length) {
      setScope('global');
    }
  }, [scope, zones.length]);

  // Highlight zone on keyboard when zone scope is selected
  useEffect(() => {
    if (typeof scope === 'number') {
      const zone = zones[scope];
      if (zone) setZonePreview(zone.bounds);
    } else {
      setZonePreview(null);
    }
    return () => setZonePreview(null);
  }, [scope, zones, setZonePreview]);

  // Determine what we're editing
  const isZoneScope = typeof scope === 'number';
  const selectedZone = isZoneScope ? zones[scope] : undefined;
  const hasZoneMapping = selectedZone?.note_mapping !== undefined;

  // Get current layer
  const currentLayer = layers.find((l) => l.id === activeLayer);
  const hasLayerMapping = currentLayer?.note_mapping !== undefined;

  // Get the effective mapping to display/edit
  let effectiveMapping: NoteMapping;
  let editTarget: 'global' | 'layer' | 'zone';

  if (isZoneScope) {
    effectiveMapping = selectedZone?.note_mapping ?? globalMapping;
    editTarget = 'zone';
  } else {
    effectiveMapping = currentLayer?.note_mapping ?? globalMapping;
    editTarget = (activeLayer === 0 || !hasLayerMapping) ? 'global' : 'layer';
  }

  const baseNoteName = getNoteName(effectiveMapping.base_note);

  // Track selected preset for showing explanation
  const [selectedPreset, setSelectedPreset] = useState<keyof typeof presetMappings | null>(null);

  // Auto-detect preset when mapping changes
  useEffect(() => {
    const detected = detectPreset(effectiveMapping);
    setSelectedPreset(detected);
  }, [effectiveMapping.intervals.q, effectiveMapping.intervals.r]);

  // Determine if the mapping editor should be shown
  const showEditor = isZoneScope ? hasZoneMapping : (activeLayer === 0 || hasLayerMapping);

  // --- Handlers ---

  const applyMapping = (mapping: NoteMapping) => {
    if (editTarget === 'zone' && isZoneScope && selectedZone) {
      updateZone(scope, { ...selectedZone, note_mapping: mapping });
    } else if (editTarget === 'layer') {
      setLayerNoteMapping(activeLayer, mapping);
    } else {
      setNoteMapping(mapping);
    }
  };

  const handlePresetChange = (presetName: keyof typeof presetMappings) => {
    if (!presetMappings[presetName]) return;
    applyMapping(presetMappings[presetName]);
  };

  const handleOriginChange = (q: number, r: number) => {
    if (editTarget === 'global' && activeLayer === 0) {
      updateOrigin(q, r);
    } else {
      applyMapping({ ...effectiveMapping, origin: { q, r } });
    }
  };

  const handleBaseChange = (baseNote: number) => {
    if (editTarget === 'global' && activeLayer === 0) {
      updateBase(baseNote);
    } else {
      applyMapping({ ...effectiveMapping, base_note: Math.max(0, Math.min(127, baseNote)) });
    }
  };

  const handleIntervalsChange = (q: number, r: number) => {
    if (editTarget === 'global' && activeLayer === 0) {
      updateIntervals(q, r);
    } else {
      applyMapping({
        ...effectiveMapping,
        intervals: {
          q: Math.max(-127, Math.min(127, q)),
          r: Math.max(-127, Math.min(127, r)),
        },
      });
    }
  };

  const handleEnableLayerMapping = (enabled: boolean) => {
    if (enabled) {
      setLayerNoteMapping(activeLayer, { ...globalMapping });
    } else {
      setLayerNoteMapping(activeLayer, null);
    }
  };

  const handleEnableZoneMapping = (enabled: boolean) => {
    if (!isZoneScope || !selectedZone) return;
    if (enabled) {
      // Copy global mapping as starting point
      updateZone(scope, { ...selectedZone, note_mapping: { ...globalMapping } });
    } else {
      // Remove zone mapping (fall back to global)
      const { note_mapping: _, ...zoneWithout } = selectedZone;
      updateZone(scope, zoneWithout as typeof selectedZone);
    }
  };

  return (
    <>
      {/* Zone scope selector (only when zones exist) */}
      {zones.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <label
            htmlFor="mapping-scope"
            style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 'bold' }}
          >
            Scope:
          </label>
          <select
            id="mapping-scope"
            value={typeof scope === 'number' ? scope.toString() : 'global'}
            onChange={(e) => {
              const val = e.target.value;
              setScope(val === 'global' ? 'global' : parseInt(val, 10));
            }}
            style={{
              width: '100%',
              padding: '6px',
              fontSize: '14px',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
            }}
          >
            <option value="global">Global</option>
            {zones.map((z, i) => (
              <option key={i} value={i.toString()}>
                Zone {i + 1}: {z.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Zone custom mapping toggle */}
      {isZoneScope && selectedZone && (
        <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '6px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hasZoneMapping}
              onChange={(e) => handleEnableZoneMapping(e.target.checked)}
            />
            <span style={{ fontSize: '14px' }}>
              Use zone-specific note mapping
            </span>
          </label>
          {!hasZoneMapping && (
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
              Currently using global mapping
            </div>
          )}
        </div>
      )}

      {/* Per-layer mapping toggle (only in global scope, non-default layers) */}
      {!isZoneScope && activeLayer !== 0 && (
        <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#e9ecef', borderRadius: '6px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={hasLayerMapping}
              onChange={(e) => handleEnableLayerMapping(e.target.checked)}
            />
            <span style={{ fontSize: '14px' }}>
              Use layer-specific note mapping
            </span>
          </label>
          {!hasLayerMapping && (
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
              Currently using global mapping (layer 0)
            </div>
          )}
        </div>
      )}

      {/* Mapping editor (shown when editing is active) */}
      {showEditor && (
        <>
          {/* Preset selector */}
          <div style={{ marginBottom: '16px' }}>
            <label
              htmlFor="preset-select"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Preset Layout:
            </label>
            <select
              id="preset-select"
              value={selectedPreset ?? ''}
              onChange={(e) => {
                const presetName = e.target.value as keyof typeof presetMappings;
                if (presetName) {
                  handlePresetChange(presetName);
                  setSelectedPreset(presetName);
                } else {
                  setSelectedPreset(null);
                }
              }}
              disabled={isMicrotonal}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                ...(isMicrotonal ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
              }}
            >
              <option value="">Custom</option>
              <option value="wickiHayden">Wicki-Hayden</option>
              <option value="harmonicTable">Harmonic Table</option>
              <option value="gerhard">Gerhard</option>
              <option value="chromatic">Chromatic</option>
              <option value="wholeTone">Whole Tone</option>
              <option value="fourths">Fourths (Guitar-like)</option>
            </select>

            {/* Preset explanation */}
            {selectedPreset && presetExplanations[selectedPreset] && (
              <div
                style={{
                  marginTop: '8px',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#495057' }}>
                  {presetExplanations[selectedPreset].name}
                </div>
                <div style={{ marginBottom: '8px', color: '#6c757d', lineHeight: '1.4' }}>
                  {presetExplanations[selectedPreset].description}
                </div>
                <div
                  style={{
                    padding: '6px 8px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    marginBottom: '6px',
                  }}
                >
                  {presetExplanations[selectedPreset].intervals}
                </div>
                <div style={{ fontSize: '12px', color: '#28a745' }}>
                  <strong>Best for:</strong> {presetExplanations[selectedPreset].bestFor}
                </div>
              </div>
            )}

            {/* Custom mapping info */}
            {!selectedPreset && (
              <div
                style={{
                  marginTop: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#856404',
                }}
              >
                Custom interval configuration. Adjust q-axis and r-axis values below to create your own isomorphic layout.
              </div>
            )}
          </div>

          {/* Origin (q,r) - spec §5.3 */}
          <div style={{ marginBottom: '12px' }}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
                Origin (q,r):
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="origin-q"
                  style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}
                >
                  q:
                </label>
                <input
                  id="origin-q"
                  type="number"
                  value={effectiveMapping.origin.q}
                  onChange={(e) =>
                    handleOriginChange(parseInt(e.target.value, 10), effectiveMapping.origin.r)
                  }
                  style={{
                    width: '100%',
                    padding: '6px',
                    fontSize: '14px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="origin-r"
                  style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}
                >
                  r:
                </label>
                <input
                  id="origin-r"
                  type="number"
                  value={effectiveMapping.origin.r}
                  onChange={(e) =>
                    handleOriginChange(effectiveMapping.origin.q, parseInt(e.target.value, 10))
                  }
                  style={{
                    width: '100%',
                    padding: '6px',
                    fontSize: '14px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Base Note - spec §5.3 (0-127) */}
          <div style={{ marginBottom: '12px' }}>
            <label
              htmlFor="base-note"
              style={{ display: 'block', marginBottom: '4px', fontSize: '14px' }}
            >
              Base Note (0-127):
            </label>
            <input
              id="base-note"
              type="number"
              min="0"
              max="127"
              value={effectiveMapping.base_note}
              onChange={(e) => handleBaseChange(parseInt(e.target.value, 10))}
              style={{
                width: '100%',
                padding: '6px',
                fontSize: '14px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
              }}
            />
            <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
              {effectiveMapping.base_note} = {baseNoteName}
            </div>
          </div>

          {/* Intervals - spec §5.3 */}
          <div style={{ marginBottom: '12px' }}>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <label style={{ fontSize: '14px', fontWeight: 'bold' }}>
                Intervals (semitones):
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="interval-q"
                  style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}
                >
                  q-axis:
                </label>
                <input
                  id="interval-q"
                  type="number"
                  min="-127"
                  max="127"
                  value={effectiveMapping.intervals.q}
                  onChange={(e) =>
                    handleIntervalsChange(
                      parseInt(e.target.value, 10),
                      effectiveMapping.intervals.r
                    )
                  }
                  disabled={isMicrotonal}
                  style={{
                    width: '100%',
                    padding: '6px',
                    fontSize: '14px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    ...(isMicrotonal ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  htmlFor="interval-r"
                  style={{ display: 'block', fontSize: '12px', marginBottom: '2px' }}
                >
                  r-axis:
                </label>
                <input
                  id="interval-r"
                  type="number"
                  min="-127"
                  max="127"
                  value={effectiveMapping.intervals.r}
                  onChange={(e) =>
                    handleIntervalsChange(
                      effectiveMapping.intervals.q,
                      parseInt(e.target.value, 10)
                    )
                  }
                  disabled={isMicrotonal}
                  style={{
                    width: '100%',
                    padding: '6px',
                    fontSize: '14px',
                    border: '1px solid #e9ecef',
                    borderRadius: '4px',
                    ...(isMicrotonal ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                />
              </div>
            </div>

            {/* Microtonal mode info box */}
            {isMicrotonal && (
              <div
                style={{
                  marginTop: '10px',
                  padding: '10px',
                  backgroundColor: '#e7f3ff',
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  Microtonal mode active. Intervals configured in Tuning panel.
                </div>
                <div style={{ fontFamily: 'monospace' }}>
                  Q: {effectiveMapping.interval_q_cents ?? 0} (0.1-cent) = {((effectiveMapping.interval_q_cents ?? 0) / 10).toFixed(1)} cents
                </div>
                <div style={{ fontFamily: 'monospace' }}>
                  R: {effectiveMapping.interval_r_cents ?? 0} (0.1-cent) = {((effectiveMapping.interval_r_cents ?? 0) / 10).toFixed(1)} cents
                </div>
              </div>
            )}
          </div>

          {/* Formula display (spec §5.3) */}
          <div
            style={{
              padding: '10px 12px',
              backgroundColor: '#e9ecef',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            note = {effectiveMapping.base_note} + (q - {effectiveMapping.origin.q}) × {effectiveMapping.intervals.q} + (r - {effectiveMapping.origin.r}) × {effectiveMapping.intervals.r}
          </div>
        </>
      )}

      {/* Context info */}
      <div
        style={{
          marginTop: '12px',
          padding: '10px 12px',
          backgroundColor: '#e9ecef',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#6c757d',
        }}
      >
        {isZoneScope && selectedZone
          ? hasZoneMapping
            ? `Editing zone "${selectedZone.name}" mapping`
            : `Zone "${selectedZone.name}" uses global mapping`
          : activeLayer === 0
            ? 'Editing global note mapping (default for all layers and zones)'
            : hasLayerMapping
              ? `Editing layer "${currentLayer?.name}" specific mapping`
              : `Layer "${currentLayer?.name}" uses global mapping`}
      </div>
    </>
  );
}
